const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"] as const;

export type GtmPageEventType = "PageLoad" | "PageExit";

export type GtmDtmFields = {
    viewName?: string;
    language?: string;
    product?: string;
    zip?: string;
    state?: string;
    soa?: string;
    launchpadId?: string;
    userType?: string;
};

export const DEFAULT_ORGANIC_SOA = "";

export function shouldFireGtm(appUserType: string): boolean {
    return appUserType === "Customer";
}

export function readSoaCookie(cookieString: string = typeof document !== "undefined" ? document.cookie : ""): string {
    const cookie = cookieString.split(";").map(c => c.trim()).find(c => c.startsWith("soa="));
    if (!cookie) return "";
    return decodeURIComponent(cookie.substring(4)).trim();
}

export function resolveSoa(
    redirectSoa?: string,
    cookieSoa?: string,
    defaultSoa: string = DEFAULT_ORGANIC_SOA
): string {
    const fromRedirect = redirectSoa?.trim() ?? "";
    if (fromRedirect) return fromRedirect;
    const fromCookie = cookieSoa?.trim() ?? "";
    if (fromCookie) return fromCookie;
    return defaultSoa.trim();
}

export function mapGtmUserType(appUserType: string): string {
    if (appUserType === "Customer") return "WebCustomer";
    return "";
}

export function readUtmParams(search: string = ""): Record<string, string> {
    const params = new URLSearchParams(search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
        const value = params.get(key);
        if (value) utm[key] = value;
    }
    const soaFromQuery = params.get("soa");
    if (soaFromQuery) utm.utm_soa = soaFromQuery;
    return utm;
}

export function getPageLoadLatency(perf: Pick<Performance, "getEntriesByType" | "now"> = performance): number {
    const nav = perf.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav && nav.loadEventEnd > 0) return Math.round(nav.loadEventEnd);
    return Math.round(perf.now());
}

export function buildPageViewEvent(
    input: GtmDtmFields,
    opts: {
        eventType: GtmPageEventType;
        previousPageName: string;
        pageLoadLatency?: number;
        timeOnPage?: number;
        isDomLoad?: boolean;
        utm?: Record<string, string>;
    }
): Record<string, unknown> {
    const event: Record<string, unknown> = {
        eventType: opts.eventType,
        pageName: input.viewName || "",
        previousPageName: opts.previousPageName || "",
        language: input.language || "",
    };

    if (opts.eventType === "PageLoad" && opts.pageLoadLatency != null) {
        event.pageLoadLatency = opts.pageLoadLatency;
    }
    if (opts.eventType === "PageExit" && opts.timeOnPage != null) {
        event.timeOnPage = opts.timeOnPage;
    }
    if (opts.isDomLoad != null) event.isDomLoad = opts.isDomLoad;
    if (input.userType) event.userType = input.userType;
    if (input.soa) event.soa = input.soa;
    if (input.product) event.product = input.product;
    if (input.zip) event.zip = input.zip;
    if (input.state) event.state = input.state;
    if (input.launchpadId) event.launchpadId = input.launchpadId;
    if (opts.utm) Object.assign(event, opts.utm);

    return event;
}
